import { Circle, Ellipse, Group, Line, Rect } from "react-konva";
import type Konva from "konva";
import type {
  CircleObject,
  DimObject,
  FormationObject,
  SpotlightObject,
} from "@/lib/canvas/telestrator-schema";
import { LEGACY_DEFAULT_COLOR } from "@/lib/canvas/shared-shape";
import { pointToPx, type Dims } from "@/lib/canvas/coords";
import { translatePoints } from "@/lib/canvas/shape-edit";

/**
 * Renderers for the three telestrator-only overlay kinds: spotlight,
 * dim, and formation. Imported by `TelestratorCanvas.tsx`. Each
 * renderer mirrors the legacy painter's behavior at
 * `js/coaching/review.js:1283-1359`.
 *
 * The spotlight is the most interesting case — it draws a full-frame
 * darkening overlay with a circular hole punched at the spotlight's
 * center via `globalCompositeOperation: "destination-out"`. Konva
 * doesn't have a declarative spotlight primitive, so this uses a
 * custom `sceneFunc` on a `Group`: fill the rect, switch composite
 * mode, draw the ellipse hole, switch composite mode back. The same
 * trick mirrors how Konva docs recommend implementing custom
 * compositing on a single layer without `cache()` overhead.
 */

const SELECTED_COLOR = "#fbbf24";

export type SpotlightRendererProps = {
  shape: SpotlightObject;
  dims: Dims;
  selected?: boolean;
  /** Select via the bounded group (ring / bounds). */
  onSelect?: () => void;
  /** Select via the full-frame darkening (the `frame-catcher`
   * rect). The editor gates this on the Select tool so a drawing
   * gesture that ends over the darkening doesn't steal selection
   * from the freshly drawn shape. */
  onFrameSelect?: () => void;
  draggable?: boolean;
  onDragEnd?: (shape: SpotlightObject) => void;
};

/** Spotlight — full-frame darkening overlay with a circular hole at
 * (x+w/2, y+h/2).
 *
 * Konva supports `globalCompositeOperation` as a first-class prop on
 * every Node, so the darkening + hole punch are two plain nodes:
 *   1. A `<Rect>` at 48% black filling the entire frame.
 *   2. A `<Circle>` with `globalCompositeOperation="destination-out"`
 *      that erases the alpha of (1) inside the hole region.
 *
 * The interactive surface is a SEPARATE positioned `<Group>` over the
 * spotlight's `(x, y, w, h)` bounds — the zone pattern — so the
 * editor's Transformer can attach to it for resize and drags read
 * `e.target.x()/y()` directly. During a drag/transform the hole
 * circle is synced Konva-side (looked up by `${id}__hole`) so the
 * darkening follows live without any React state writes; the commit
 * on drag/transform-end reconciles React to the same values.
 *
 * Hit-detection: the full-frame `name="frame-catcher"` rect keeps
 * "click the darkening to select" working (PR #6), while its name
 * lets the editor's drawing gate treat it as background so a coach
 * can still draw new shapes over a placed spotlight. */
export function SpotlightRenderer(props: SpotlightRendererProps) {
  const { shape, dims, selected, onSelect, onFrameSelect, draggable, onDragEnd } =
    props;
  const color = selected ? SELECTED_COLOR : shape.color ?? LEGACY_DEFAULT_COLOR;
  const tl = { x: shape.x * dims.width, y: shape.y * dims.height };
  const w = shape.w * dims.width;
  const h = shape.h * dims.height;
  const holeRadius = Math.max(8, Math.min(w / 2, h / 2));
  const ringStrokeWidth = shape.strokeWidth ?? 3;
  const holeId = `${shape.id ?? "spotlight-pending"}__hole`;

  // Live Konva-side sync of the hole during a drag or Transformer
  // resize — no React state until the gesture commits.
  const syncHole = (node: Konva.Node) => {
    const hole = node.getStage()?.findOne(`#${holeId}`) as Konva.Circle | undefined;
    if (!hole) return;
    const liveW = w * node.scaleX();
    const liveH = h * node.scaleY();
    hole.position({ x: node.x() + liveW / 2, y: node.y() + liveH / 2 });
    hole.radius(Math.max(8, Math.min(liveW / 2, liveH / 2)));
  };

  return (
    <>
      {/* 1+2. Full-frame darkening + destination-out hole punch.
       * Purely visual — never listens. */}
      <Group listening={false}>
        <Rect
          x={0}
          y={0}
          width={dims.width}
          height={dims.height}
          fill="rgba(0, 0, 0, 0.48)"
        />
        <Circle
          id={holeId}
          x={tl.x + w / 2}
          y={tl.y + h / 2}
          radius={holeRadius}
          fill="rgba(0, 0, 0, 1)"
          globalCompositeOperation="destination-out"
        />
      </Group>
      {/* Full-frame click-catcher: selects when the darkening is
       * clicked; named so the editors' drawing gate treats it as
       * background while a drawing tool is armed. */}
      <Rect
        name="frame-catcher"
        x={0}
        y={0}
        width={dims.width}
        height={dims.height}
        opacity={0}
        onClick={onFrameSelect}
        onTap={onFrameSelect}
      />
      {/* Interactive bounded group — the drag + Transformer target. */}
      <Group
        id={shape.id}
        x={tl.x}
        y={tl.y}
        width={w}
        height={h}
        onClick={onSelect}
        onTap={onSelect}
        draggable={draggable}
        onDragStart={onSelect}
        onDragMove={(e) => syncHole(e.target)}
        onTransform={(e) => syncHole(e.target)}
        onDragEnd={(e) => {
          if (!onDragEnd) return;
          const x = clamp01(e.target.x() / dims.width);
          const y = clamp01(e.target.y() / dims.height);
          // Pin the node (and the hole) to the committed position:
          // when a clamped commit EQUALS the previous props,
          // react-konva sees no prop delta and would leave both
          // stranded where the pointer dropped them.
          e.target.position({ x: x * dims.width, y: y * dims.height });
          syncHole(e.target);
          onDragEnd({ ...shape, x, y });
        }}
      >
        {/* Spotlight ring so the user sees the bounds. */}
        <Circle
          x={w / 2}
          y={h / 2}
          radius={holeRadius}
          stroke={color}
          strokeWidth={ringStrokeWidth}
          strokeScaleEnabled={false}
          fillEnabled={false}
          listening={false}
        />
        {/* Bounds-sized invisible hit rect — the grab target. */}
        <Rect x={0} y={0} width={w} height={h} opacity={0} listening />
      </Group>
    </>
  );
}

export type CircleRendererProps = {
  shape: CircleObject;
  dims: Dims;
  selected?: boolean;
  onSelect?: () => void;
  draggable?: boolean;
  onDragEnd?: (shape: CircleObject) => void;
};

/** Circle — a stroked ellipse inscribed in the `(x, y, w, h)`
 * bounds. Ports legacy `js/coaching/review.js:1327-1329` (the
 * non-spotlight branch of the shared circle/spotlight render path).
 * Selection bumps the stroke + recolors to the shared selected-amber
 * so it stays obvious.
 *
 * Positioned like a zone — `<Group x y width height>` with children
 * in group-relative coords — so the editor's Transformer can attach
 * for resize and drags read `e.target.x()/y()` directly.
 *
 * Hit-detection: an invisible `<Rect>` over the bounds catches
 * clicks anywhere inside (same PR #6 fix the spotlight uses) so a
 * thin ring is still easy to grab. */
export function CircleRenderer(props: CircleRendererProps) {
  const { shape, dims, selected, onSelect, draggable, onDragEnd } = props;
  const color = selected ? SELECTED_COLOR : shape.color ?? LEGACY_DEFAULT_COLOR;
  const tl = { x: shape.x * dims.width, y: shape.y * dims.height };
  const w = shape.w * dims.width;
  const h = shape.h * dims.height;
  const strokeWidth = (shape.strokeWidth ?? 3) + (selected ? 1.5 : 0);

  return (
    <Group
      id={shape.id}
      x={tl.x}
      y={tl.y}
      width={w}
      height={h}
      onClick={onSelect}
      onTap={onSelect}
      draggable={draggable}
      onDragStart={onSelect}
      onDragEnd={(e) => {
        if (!onDragEnd) return;
        const x = clamp01(e.target.x() / dims.width);
        const y = clamp01(e.target.y() / dims.height);
        // Pin to the committed position — a value-equal clamped
        // commit produces no prop delta, so react-konva would leave
        // the node stranded where the pointer dropped it.
        e.target.position({ x: x * dims.width, y: y * dims.height });
        onDragEnd({ ...shape, x, y });
      }}
    >
      <Ellipse
        x={w / 2}
        y={h / 2}
        radiusX={Math.max(4, w / 2)}
        radiusY={Math.max(4, h / 2)}
        stroke={color}
        strokeWidth={strokeWidth}
        strokeScaleEnabled={false}
        fillEnabled={false}
        listening={false}
      />
      {/* Invisible hit-detection rect over the bounds — same pattern
       * as the spotlight so a thin ring is still grabbable. */}
      <Rect x={0} y={0} width={w} height={h} opacity={0} listening />
    </Group>
  );
}

export type DimRendererProps = {
  shape: DimObject;
  dims: Dims;
  selected?: boolean;
  onSelect?: () => void;
};

/** Dim — full-frame translucent black overlay. The simplest of the
 * three. Selection state shows a thin amber ring around the frame so
 * the user can find it to delete it.
 *
 * The full-frame rect is named `frame-catcher` so the editors'
 * drawing gate treats it as background — a coach can keep drawing
 * shapes over a placed dim. Selecting the dim itself requires the
 * Select tool (clicking it with a drawing tool armed draws instead). */
export function DimRenderer(props: DimRendererProps) {
  const { shape, dims, selected, onSelect } = props;
  return (
    <Group id={shape.id} onClick={onSelect} onTap={onSelect}>
      <Rect
        name="frame-catcher"
        x={0}
        y={0}
        width={dims.width}
        height={dims.height}
        fill={`rgba(0, 0, 0, ${shape.opacity})`}
      />
      {selected ? (
        <Rect
          x={2}
          y={2}
          width={dims.width - 4}
          height={dims.height - 4}
          stroke={SELECTED_COLOR}
          strokeWidth={3}
          dash={[6, 4]}
          listening={false}
        />
      ) : null}
    </Group>
  );
}

export type FormationRendererProps = {
  shape: FormationObject;
  dims: Dims;
  selected?: boolean;
  onSelect?: () => void;
  draggable?: boolean;
  onDragEnd?: (shape: FormationObject) => void;
};

/** Formation — anchors rendered as filled circles + the convex hull
 * as a closed polygon connecting them. The hull is precomputed at
 * save time (via `convexHull` in telestrator-schema.ts); render-time
 * just draws what's there.
 *
 * Draggable as a whole — the drag translates every anchor AND the
 * precomputed hull points together, so no hull recompute is needed.
 * Per-anchor editing is deliberately not supported yet. */
export function FormationRenderer(props: FormationRendererProps) {
  const { shape, dims, selected, onSelect, draggable, onDragEnd } = props;
  const color = selected ? SELECTED_COLOR : shape.color ?? LEGACY_DEFAULT_COLOR;
  const strokeWidth = (shape.strokeWidth ?? 2) + (selected ? 1.5 : 0);

  const hullPoints: number[] = [];
  for (const p of shape.hull_points) {
    const px = pointToPx(p, dims);
    hullPoints.push(px.x, px.y);
  }

  return (
    <Group
      id={shape.id}
      onClick={onSelect}
      onTap={onSelect}
      draggable={draggable}
      onDragStart={onSelect}
      onDragEnd={(e) => {
        if (!onDragEnd) return;
        const dx = e.target.x() / dims.width;
        const dy = e.target.y() / dims.height;
        e.target.position({ x: 0, y: 0 });
        onDragEnd({
          ...shape,
          anchors: shape.anchors.map((a) => ({
            ...a,
            x: clamp01(a.x + dx),
            y: clamp01(a.y + dy),
          })),
          hull_points: translatePoints(shape.hull_points, dx, dy),
        });
      }}
    >
      {shape.hull_points.length >= 3 ? (
        <Line
          points={hullPoints}
          closed
          stroke={color}
          strokeWidth={strokeWidth}
          fill={`${hexToRgba(color, 0.16)}`}
          dash={[8, 5]}
        />
      ) : null}
      {shape.anchors.map((a, i) => {
        const center = pointToPx(a, dims);
        return (
          <Group key={`${a.player_id ?? "anchor"}-${i}`} x={center.x} y={center.y}>
            <Circle radius={10} fill={color} stroke="rgba(15, 23, 42, 0.6)" strokeWidth={1.5} />
            {a.label ? (
              <Line
                points={[]}
                listening={false}
                // Label text rendered separately to keep this overlay
                // light; a future revision can add a centered <Text>
                // here. The legacy painter renders the jersey number
                // inline; for M3 we keep formations minimal so the
                // demo can ship.
              />
            ) : null}
          </Group>
        );
      })}
    </Group>
  );
}

function hexToRgba(hex: string, alpha: number): string {
  if (!/^#[0-9a-fA-F]{6}$/.test(hex)) return `rgba(148, 163, 184, ${alpha})`;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function clamp01(v: number): number {
  if (!Number.isFinite(v)) return 0;
  if (v < 0) return 0;
  if (v > 1) return 1;
  return v;
}
